/**
 * Purchase Order Cancel Screen (4.3.5)
 * Procurement Module - Orders
 * UI Layout: EXACT MATCH to PDF specification
 * 
 * Layout: PO selection with cancel reason
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, AlertCircle, Search, X, AlertTriangle } from 'lucide-react';

const PurchaseOrderCancelScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  
  // Form state
  const [formData, setFormData] = useState({
    poNo: '',
    poDate: '',
    supplierName: '',
    totalItems: '0',
    totalQty: '0.000',
    totalValue: '0.00',
    cancelReason: '',
    cancelDate: new Date().toISOString().split('T')[0],
  });

  const [poList, setPoList] = useState([]);
  const [poItems, setPoItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLookup, setShowLookup] = useState(false);

  // Theme colors
  const bgColor = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setPoList([
        { poNo: 'PO101L0001', poDate: '2024-01-15', supplier: 'FASHION FOREC', items: 5, qty: '150.000', value: '225000.00', status: 'Open' },
        { poNo: 'PO101L0002', poDate: '2024-01-16', supplier: 'BALAJI ENTERPRISES', items: 3, qty: '80.000', value: '120000.00', status: 'Partial' },
        { poNo: 'PO101L0003', poDate: '2024-01-18', supplier: 'KRISHNA TEXTILES', items: 8, qty: '200.000', value: '300000.00', status: 'Open' },
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectPO = (po) => {
    setFormData({
      poNo: po.poNo,
      poDate: po.poDate,
      supplierName: po.supplier,
      totalItems: String(po.items),
      totalQty: po.qty,
      totalValue: po.value,
      cancelReason: '',
      cancelDate: new Date().toISOString().split('T')[0],
    });
    
    // Load PO items
    setPoItems([
      { id: 1, itemCode: 'IT10001', itemName: 'MENS FORMAL SHIRT', qty: '50.000', rate: '1500.00', value: '75000.00' },
      { id: 2, itemCode: 'IT10002', itemName: 'LADIES TOP', qty: '30.000', rate: '800.00', value: '24000.00' },
    ]);
    
    setShowLookup(false);
  };

  const handleCancel = () => {
    if (!formData.poNo) {
      setError('Please select a PO to cancel');
      return;
    }
    if (!formData.cancelReason) {
      setError('Please provide a cancel reason');
      return;
    }
    
    if (window.confirm(`Are you sure you want to cancel PO ${formData.poNo}? This action cannot be undone.`)) {
      console.log('Cancelling PO:', formData);
      alert('Purchase Order cancelled successfully!');
    }
  };

  // Input field component
  const InputField = ({ label, name, value, onChange, type = 'text', disabled, hasSearch }) => (
    <div className="flex items-center mb-2">
      <label className={`w-28 text-xs ${labelColor}`}>{label}</label>
      <div className="flex-1 flex items-center gap-1">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
          className={`w-full px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor} ${disabled ? 'opacity-60' : ''}`}
          data-testid={`input-${name}`}
        />
        {hasSearch && (
          <button className={`p-1 rounded border ${borderColor} ${inputBg} hover:bg-blue-500/20`} onClick={() => setShowLookup(true)}>
            <Search size={12} className="text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-4`} data-testid="purchase-order-cancel-screen">
      {/* Header */}
      <div className={`${cardBg} rounded-lg p-3 mb-4 border ${borderColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Purchase Order Cancel</h1>
            <p className="text-xs text-gray-400">Screen ID: 4.3.5 | Procurement Module</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleCancel} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-sm" data-testid="cancel-btn" disabled={!formData.poNo}>
              <AlertTriangle size={16} /> Cancel PO
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-400" size={18} />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}

      {/* Warning Banner */}
      <div className={`mb-4 p-3 bg-orange-500/20 border border-orange-500/50 rounded-lg flex items-center gap-2`}>
        <AlertTriangle className="text-orange-400" size={18} />
        <span className="text-orange-400 text-sm">Warning: Cancelling a Purchase Order is irreversible. Please ensure all details are correct before proceeding.</span>
      </div>

      {/* Main Form */}
      <div className={`${cardBg} rounded-lg border ${borderColor} p-4`}>
        <div className="grid grid-cols-2 gap-4">
          {/* Left - PO Selection */}
          <div className={`${sectionBg} rounded-lg p-4 border ${borderColor}`}>
            <h3 className={`text-sm font-semibold mb-3 pb-2 border-b ${borderColor}`}>PO Information</h3>
            
            <InputField label="PO No" name="poNo" value={formData.poNo} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} hasSearch />
            <InputField label="PO Date" name="poDate" value={formData.poDate} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} disabled />
            <InputField label="Supplier" name="supplierName" value={formData.supplierName} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} disabled />
            <InputField label="Total Items" name="totalItems" value={formData.totalItems} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} disabled />
            <InputField label="Total Qty" name="totalQty" value={formData.totalQty} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} disabled />
            <InputField label="Total Value" name="totalValue" value={formData.totalValue} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} disabled />
          </div>

          {/* Right - Cancel Details */}
          <div className={`${sectionBg} rounded-lg p-4 border ${borderColor}`}>
            <h3 className={`text-sm font-semibold mb-3 pb-2 border-b ${borderColor}`}>Cancel Details</h3>
            
            <InputField label="Cancel Date" name="cancelDate" value={formData.cancelDate} onChange={(n, v) => setFormData(p => ({ ...p, [n]: v }))} type="date" />
            
            <div className="flex items-start mb-2">
              <label className={`w-28 text-xs ${labelColor}`}>Cancel Reason <span className="text-red-500">*</span></label>
              <textarea
                value={formData.cancelReason}
                onChange={(e) => setFormData(p => ({ ...p, cancelReason: e.target.value }))}
                rows={4}
                className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
                placeholder="Enter reason for cancellation..."
                data-testid="input-cancelReason"
              />
            </div>
          </div>
        </div>

        {/* PO Items Grid */}
        {poItems.length > 0 && (
          <div className={`${sectionBg} rounded-lg p-3 border ${borderColor} mt-4`}>
            <h3 className={`text-xs font-semibold mb-3 pb-1 border-b ${borderColor}`}>PO Items (Will be cancelled)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                  <tr>
                    <th className="px-2 py-1 text-left">Sl.No</th>
                    <th className="px-2 py-1 text-left">Item Code</th>
                    <th className="px-2 py-1 text-left">Item Name</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-right">Rate</th>
                    <th className="px-2 py-1 text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {poItems.map((item, index) => (
                    <tr key={item.id} className={`border-t ${borderColor}`}>
                      <td className="px-2 py-1">{index + 1}</td>
                      <td className="px-2 py-1 font-mono">{item.itemCode}</td>
                      <td className="px-2 py-1">{item.itemName}</td>
                      <td className="px-2 py-1 text-right">{item.qty}</td>
                      <td className="px-2 py-1 text-right">{item.rate}</td>
                      <td className="px-2 py-1 text-right">{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* PO Lookup Modal */}
      {showLookup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`${cardBg} rounded-lg p-4 w-[600px] border ${borderColor}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Select Purchase Order</h3>
              <button onClick={() => setShowLookup(false)}><X size={18} /></button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
                  <tr>
                    <th className="px-2 py-1 text-left">PO No</th>
                    <th className="px-2 py-1 text-left">PO Date</th>
                    <th className="px-2 py-1 text-left">Supplier</th>
                    <th className="px-2 py-1 text-right">Items</th>
                    <th className="px-2 py-1 text-right">Value</th>
                    <th className="px-2 py-1 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {poList.map(po => (
                    <tr 
                      key={po.poNo} 
                      className={`border-t ${borderColor} cursor-pointer hover:${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50'}`}
                      onClick={() => handleSelectPO(po)}
                    >
                      <td className="px-2 py-2 font-mono text-blue-400">{po.poNo}</td>
                      <td className="px-2 py-2">{po.poDate}</td>
                      <td className="px-2 py-2">{po.supplier}</td>
                      <td className="px-2 py-2 text-right">{po.items}</td>
                      <td className="px-2 py-2 text-right">{po.value}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          po.status === 'Open' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-orange-500/20 text-orange-400'
                        }`}>
                          {po.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderCancelScreen;
